import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription, FEATURE_DESCRIPTIONS } from '../contexts/SubscriptionContext';
import '../styles/PremiumGate.css';

/**
 * PremiumGate Component
 *
 * Wraps content that requires premium access.
 * Shows a blurred preview with upgrade prompt for free users.
 *
 * Props:
 * - feature: string - The feature key (e.g., 'photos', 'mapView')
 * - children: ReactNode - The content to gate
 * - preview: ReactNode (optional) - Custom preview content
 * - blurAmount: number (optional) - Blur intensity in pixels (default: 8)
 * - showOverlay: boolean (optional) - Whether to show the upgrade overlay (default: true)
 * - compact: boolean (optional) - Use compact styling for inline elements
 * - onUpgradeClick: function (optional) - Custom handler for upgrade button
 */
const PremiumGate = ({
  feature,
  children,
  preview,
  blurAmount = 8,
  showOverlay = true,
  compact = false,
  onUpgradeClick,
}) => {
  const { canAccess } = useSubscription();
  const navigate = useNavigate();

  // If user has access, just render children
  if (canAccess(feature)) {
    return <>{children}</>;
  }

  const featureInfo = FEATURE_DESCRIPTIONS[feature] || {
    name: 'Premium Feature',
    description: 'Upgrade to access this feature',
  };

  const handleUpgradeClick = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      navigate('/settings/premium');
    }
  };

  if (compact) {
    return (
      <div className="premium-gate premium-gate--compact">
        <div
          className="premium-gate__preview"
          style={{ filter: `blur(${blurAmount}px)` }}
        >
          {preview || children}
        </div>
        {showOverlay && (
          <div className="premium-gate__overlay premium-gate__overlay--compact">
            <button
              className="premium-gate__button premium-gate__button--compact"
              onClick={handleUpgradeClick}
            >
              Upgrade
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="premium-gate">
      <div
        className="premium-gate__preview"
        style={{ filter: `blur(${blurAmount}px)` }}
      >
        {preview || children}
      </div>
      {showOverlay && (
        <div className="premium-gate__overlay">
          <div className="premium-gate__content">
            <div className="premium-gate__icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <h3 className="premium-gate__title">{featureInfo.name}</h3>
            <p className="premium-gate__description">{featureInfo.description}</p>
            <button
              className="premium-gate__button"
              onClick={handleUpgradeClick}
            >
              Upgrade to Premium
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * UpgradePrompt Component
 *
 * Inline prompt shown when user hits a limit.
 *
 * Props:
 * - type: string - What was limited (e.g., 'lists', 'places')
 * - current: number - Current count
 * - limit: number - The limit they hit
 * - onUpgradeClick: function (optional) - Custom handler
 */
export const UpgradePrompt = ({ type, current, limit, onUpgradeClick }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      navigate('/settings/premium');
    }
  };

  return (
    <div className="upgrade-prompt">
      <div className="upgrade-prompt__icon">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
            fill="currentColor"
          />
        </svg>
      </div>
      <div className="upgrade-prompt__text">
        <p className="upgrade-prompt__message">
          You've reached the free limit of {limit} {type}.
        </p>
        <p className="upgrade-prompt__cta">
          Upgrade to Premium for unlimited {type}.
        </p>
      </div>
      <button className="upgrade-prompt__button" onClick={handleClick}>
        Upgrade
      </button>
    </div>
  );
};

/**
 * PremiumBadge Component
 *
 * Small badge to indicate premium features in UI.
 */
export const PremiumBadge = ({ small = false }) => {
  return (
    <span className={`premium-badge ${small ? 'premium-badge--small' : ''}`}>
      <svg
        width={small ? 10 : 12}
        height={small ? 10 : 12}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
          fill="currentColor"
        />
      </svg>
      {!small && <span>Premium</span>}
    </span>
  );
};

export default PremiumGate;
